import { getSessionToken } from './auth.js';

const BASE = import.meta.env.VITE_API_BASE_URL || '';

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message || code);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function send(path, { method = 'GET', body, formData } = {}) {
  const token = await getSessionToken();
  const headers = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';
  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: formData || (body === undefined ? undefined : JSON.stringify(body)),
  });
  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new ApiError(response.status, payload.error || 'request_failed', payload.message, payload.details);
  }
  return payload;
}

export const api = {
  session: () => send('/api/session'),
  memberHome: () => send('/api/member/home'),
  item: (id) => send(`/api/items/${id}`),
  itemAudit: (id) => send(`/api/items/${id}/audit`),
  issueItem: (payload) => send('/api/items/issue', { method: 'POST', body: payload }),
  correctItem: (id, payload) => send(`/api/items/${id}/correct`, { method: 'POST', body: payload }),
  reviseItem: (id, payload) => send(`/api/items/${id}/revise`, { method: 'POST', body: payload }),
  cancelItem: (id, payload) => send(`/api/items/${id}/cancel`, { method: 'POST', body: payload }),
  adjustDue: (id, payload) => send(`/api/dues/${id}/adjust`, { method: 'POST', body: payload }),
  submitPayment: (formData) => send('/api/submissions', { method: 'POST', formData }),
  confirmSubmission: (id) => send(`/api/submissions/${id}/confirm`, { method: 'POST', body: {} }),
  returnSubmission: (id, payload) => send(`/api/submissions/${id}/return`, { method: 'POST', body: payload }),
  pendingSubmissions: () => send('/api/submissions/pending'),
  proofAccess: (reference) => send(`/api/proofs/${encodeURIComponent(reference)}/access`),
  uploadBillProof: (formData) => send('/api/bill-proofs', { method: 'POST', formData }),
  balances: () => send('/api/balances'),
  returnBalance: (id, payload) => send(`/api/balances/${id}/return`, { method: 'POST', body: payload }),
  transferBalance: (id, payload) => send(`/api/balances/${id}/transfer`, { method: 'POST', body: payload }),
  recurrences: () => send('/api/recurrences'),
  createRecurrence: (payload) => send('/api/recurrences', { method: 'POST', body: payload }),
  updateRecurrence: (id, payload) => send(`/api/recurrences/${id}/update`, { method: 'POST', body: payload }),
  disableRecurrence: (id, payload) => send(`/api/recurrences/${id}/disable`, { method: 'POST', body: payload || {} }),
  runRecurrences: (payload) => send('/api/recurrences/run', { method: 'POST', body: payload || {} }),
  equipment: (unitId) => send(`/api/equipment${unitId ? `?unit_id=${encodeURIComponent(unitId)}` : ''}`),
  equipmentDetail: (id) => send(`/api/equipment/${id}`),
  createEquipment: (payload) => send('/api/equipment', { method: 'POST', body: payload }),
  members: () => send('/api/members'),
  inviteMember: (payload) => send('/api/members/invite', { method: 'POST', body: payload }),
  changeRole: (id, payload) => send(`/api/members/${id}/role`, { method: 'POST', body: payload }),
  ledger: (filters) => send(`/api/ledger?${new URLSearchParams(
    Object.entries(filters || {}).filter(([, value]) => value),
  )}`),
  uploadHistoricalProof: (formData) => send('/api/historical/proofs', { method: 'POST', formData }),
  previewHistorical: (payload) => send('/api/historical/records/preview', { method: 'POST', body: payload }),
  createHistorical: (payload) => send('/api/historical/records', { method: 'POST', body: payload }),
  correctHistorical: (id, payload) => send(`/api/historical/records/${id}/correct`, { method: 'POST', body: payload }),
  historicalRecords: () => send('/api/historical/records'),
  historicalHistory: (id) => send(`/api/historical/records/${id}/history`),
  recordExport: (payload) => send('/api/exports/record', { method: 'POST', body: payload }),
  exports: () => send('/api/exports'),
};
